import {
  useBrokerageCard,
  useDistributorProfile,
  useFundFiltersMetadata,
} from "creso-core/hooks";
import { useCallback, useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  DistinctFilterMetadata,
  ProductType,
  UnboxPaginatedResponse,
} from "creso-core/types";
import { getProductLabel } from "creso-core/constants";
import {
  DataTable,
  ScrollArea,
  DataTablePagination,
  V2Loader,
  Tabs,
  TabsList,
  TabsTrigger,
  SearchInput,
  SortableHeader,
} from "web-design-system";
import { useDebounceValue } from "usehooks-ts";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { Page } from "@core/components/layout/page";
import { FilterList } from "@core/components/filter-list";
import { Link } from "@tanstack/react-router";
import { BrokerageStatementDialog } from "../components/brokerage-statement-dialog";

export default function ReportsPage() {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [search, setSearch] = useState("");

  const [filterSearch, setFilterSearch] = useState("");
  const [amcFilter, setAmcFilter] = useState<string[]>([]);
  const [productTypeFilter, setProductTypeFilter] = useState<string[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data: profile } = useDistributorProfile();

  const [includeGst, setIncludeGst] = useState(false);

  const handleAmcFilterChange = useCallback((_: string, values: string[]) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
    setAmcFilter((prev) => {
      const prevValues = [...prev];
      if (prevValues.includes(values[0])) {
        prevValues.splice(prevValues.indexOf(values[0]), 1);
      } else {
        prevValues.push(values[0]);
      }
      return prevValues;
    });
  }, []);

  const handleProductTypeFilterChange = useCallback(
    (_: string, values: string[]) => {
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
      }));
      setProductTypeFilter((prev) => {
        const prevValues = [...prev];
        if (prevValues.includes(values[0])) {
          prevValues.splice(prevValues.indexOf(values[0]), 1);
        } else {
          prevValues.push(values[0]);
        }
        return prevValues;
      });
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setAmcFilter([]);
    setProductTypeFilter([]);
    setFilterSearch("");
  }, []);

  const [debouncedSearch] = useDebounceValue(search, 300);

  const { data, isLoading } = useBrokerageCard(
    pagination.pageIndex + 1,
    pagination.pageSize,
    debouncedSearch,
    amcFilter,
    productTypeFilter,
    sorting
  );

  const columnHelper =
    createColumnHelper<
      UnboxPaginatedResponse<ReturnType<typeof useBrokerageCard>["data"]>
    >();

  const gstMultiplier = includeGst ? 1.18 : 1;

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("scheme_name", {
        header: "Scheme Name",
        minSize: 300,
        enableSorting: false,
        meta: {
          className: "px-4 py-3",
        },
        cell: ({ row }) => {
          return (
            <div className="flex flex-row gap-2 items-center">
              <AmcLogo amc_code={row.original.morningstar_amc_code} />
              {row.original.fund_id ? (
                <Link
                  to={"/funds/$fund_id"}
                  preloadDelay={1000}
                  params={{ fund_id: row.original.fund_id }}
                >
                  {row.original.scheme_name}
                </Link>
              ) : (
                <span>{row.original.scheme_name}</span>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor("year_1_rate", {
        header: ({ column }) => (
          <SortableHeader column={column}>Year 1 Rate</SortableHeader>
        ),
        meta: {
          className: "px-4 py-3",
        },
        cell(info) {
          return (info.getValue() * gstMultiplier).toFixed(2);
        },
      }),
      columnHelper.accessor("year_2_rate", {
        header: ({ column }) => (
          <SortableHeader column={column}>Year 2 Rate</SortableHeader>
        ),
        meta: {
          className: "px-4 py-3",
        },
        cell(info) {
          return (info.getValue() * gstMultiplier).toFixed(2);
        },
      }),
      columnHelper.accessor("year_3_rate", {
        header: ({ column }) => (
          <SortableHeader column={column}>Year 3 Rate</SortableHeader>
        ),
        meta: {
          className: "px-4 py-3",
        },
        cell(info) {
          return (info.getValue() * gstMultiplier).toFixed(2);
        },
      }),
      columnHelper.accessor("year_4_rate", {
        header: ({ column }) => (
          <SortableHeader column={column}>Year 4 Rate</SortableHeader>
        ),
        meta: {
          className: "px-4 py-3",
        },
        cell(info) {
          return (info.getValue() * gstMultiplier).toFixed(2);
        },
      }),
      columnHelper.accessor("year_5_rate", {
        header: ({ column }) => (
          <SortableHeader column={column}>Year 5 Rate</SortableHeader>
        ),
        meta: {
          className: "px-4 py-3",
        },
        cell(info) {
          return (info.getValue() * gstMultiplier).toFixed(2);
        },
      }),
    ];
  }, [columnHelper, gstMultiplier]);

  const brokerage = useMemo(() => data?.results ?? [], [data]);

  const { data: filterMetadata } = useFundFiltersMetadata([
    "provider_company_name",
    "product_type",
  ]);

  const amcFilterValues = useMemo(
    () =>
      filterMetadata
        ?.filter<DistinctFilterMetadata>((f) => f.type === "distinct")
        ?.find((f) => f.field === "provider_company_name")
        ?.values?.filter((v) =>
          v.name.toLocaleLowerCase().includes(filterSearch.toLocaleLowerCase())
        ) ?? [],
    [filterMetadata, filterSearch]
  );

  const productTypeFilterValues = useMemo(() => {
    const values =
      filterMetadata
        ?.filter<DistinctFilterMetadata>((f) => f.type === "distinct")
        ?.find((f) => f.field === "product_type")
        ?.values?.filter((v) =>
          getProductLabel(v.name as ProductType)
            .toLocaleLowerCase()
            .includes(filterSearch.toLocaleLowerCase())
        ) ?? [];

    const mf = values.find((v) => v.name === "MF");
    const sif = values.find((v) => v.name === "SIF");
    const gift = values.find((v) => v.name === "GIFT");
    return [mf, sif, gift].filter((v) => v != null);
  }, [filterMetadata, filterSearch]);

  const table = useReactTable({
    data: brokerage,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount: data?.count,
    state: {
      pagination,
      sorting,
    },
    onSortingChange: setSorting,
    manualSorting: true,
  });

  const filterListFilters = useMemo(() => {
    return [
      {
        title: "Product",
        options: productTypeFilterValues.map((v) => ({
          id: v.id,
          label: getProductLabel(v.name as ProductType),
          checked: productTypeFilter.includes(v.id),
          onCheckedChange: () => handleProductTypeFilterChange(v.id, [v.id]),
        })),
      },
      {
        title: "AMC",
        options: amcFilterValues.map((v) => ({
          id: v.id,
          label: v.name,
          checked: amcFilter.includes(v.id),
          onCheckedChange: () => handleAmcFilterChange(v.id, [v.id]),
        })),
      },
    ];
  }, [
    amcFilterValues,
    amcFilter,
    handleAmcFilterChange,
    productTypeFilterValues,
    productTypeFilter,
    handleProductTypeFilterChange,
  ]);

  return (
    <>
      <Page.Header
        title="Brokerage Structure"
        actions={<BrokerageStatementDialog />}
      />
      <Page.Body className="overflow-hidden flex-row gap-2">
        <div className="hidden self-stretch lg:block">
          <FilterList
            onClearAll={handleClearFilters}
            showSearch
            searchValue={filterSearch}
            onSearchChange={setFilterSearch}
            filters={filterListFilters}
            defaultOpenFilters={[]}
          />
        </div>
        <div className="flex flex-col gap-2 flex-1">
          <div className="flex items-center gap-2">
            <div className="w-full flex flex-row items-center gap-2">
              <SearchInput
                placeholder="Search"
                className="w-80"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
              />
              <div className="lg:hidden">
                <FilterList
                  onClearAll={handleClearFilters}
                  showSearch
                  searchValue={filterSearch}
                  onSearchChange={setFilterSearch}
                  filters={filterListFilters}
                  defaultOpenFilters={[]}
                />
              </div>
            </div>
            <ConditionalRenderer condition={!!profile?.gst_number}>
              <Tabs
                onValueChange={(v) => setIncludeGst(v === "inc")}
                value={includeGst ? "inc" : "excl"}
              >
                <TabsList>
                  <TabsTrigger value="excl">Excl. GST</TabsTrigger>
                  <TabsTrigger value="inc">Incl. GST</TabsTrigger>
                </TabsList>
              </Tabs>
            </ConditionalRenderer>
          </div>
          <V2Loader
            isLoading={isLoading}
            className="flex flex-col flex-1 w-full overflow-hidden border border-border rounded-lg"
          >
            <ScrollArea className="flex-1 flex flex-col">
              <DataTable
                pagination={false}
                table={table}
                className="border-none"
              />
            </ScrollArea>
            <DataTablePagination
              table={table}
              className="sticky bottom-0 z-20"
            />
          </V2Loader>
        </div>
      </Page.Body>
    </>
  );
}
